import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { SDKConfig, ApiResponse } from './types';
import { friendlyApiMessage } from './errorMessage';

export class HRIAClient {
  private axiosInstance: AxiosInstance;
  private getToken?: SDKConfig['getToken'];
  private onUnauthorized?: SDKConfig['onUnauthorized'];
  private getRequestHeaders?: SDKConfig['getRequestHeaders'];

  constructor(config: SDKConfig) {
    this.getToken = config.getToken;
    this.onUnauthorized = config.onUnauthorized;
    this.getRequestHeaders = config.getRequestHeaders;
    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: 20000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.axiosInstance.interceptors.request.use((cfg: any) => {
      cfg.headers = cfg.headers || {};
      const token = this.getToken ? this.getToken() : null;
      if (token) {
        (cfg.headers as any)['Authorization'] = `Bearer ${token}`;
      }
      if (this.getRequestHeaders) {
        const extra = this.getRequestHeaders();
        for (const [key, value] of Object.entries(extra)) {
          if (value) (cfg.headers as any)[key] = value;
        }
      }
      return cfg;
    });

    this.axiosInstance.interceptors.response.use(
      (res: any) => res,
      (error: any) => {
        if (error?.response?.status === 401 && this.onUnauthorized) {
          this.onUnauthorized();
        }
        // Attach a friendly, user-readable message without destroying the original.
        try {
          if (error && typeof error === 'object') {
            error.userMessage = friendlyApiMessage(error);
          }
        } catch {
          /* never let error-normalization throw */
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const res = await this.axiosInstance.get(url, { params, ...(config || {}) });
    return res.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const res = await this.axiosInstance.post(url, data, config);
    return res.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const res = await this.axiosInstance.put(url, data, config);
    return res.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const res = await this.axiosInstance.patch(url, data, config);
    return res.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const res = await this.axiosInstance.delete(url, config);
    return res.data;
  }
}
